import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { Page } from '@/components/page';
import { useFirebase } from '@/firebase';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';

export const Route = createFileRoute('/import/processing/$sessionId')({
  component: ImportProcessingPage,
});

const PIPELINE_STEPS = [
  { status: 'classifying', labelKey: 'import.step_classifying' },
  { status: 'analyzing_structure', labelKey: 'import.step_parsing' },
  { status: 'extracting_scenes', labelKey: 'import.step_scenes' },
  { status: 'extracting_entities', labelKey: 'import.step_entities' },
  { status: 'resolving_entities', labelKey: 'import.step_reconcile' },
  { status: 'extracting_relationships', labelKey: 'import.step_relationships' },
  { status: 'generating_changes', labelKey: 'import.step_changes' },
];

function ImportProcessingContent() {
  const { sessionId } = Route.useParams();
  const { novel } = useNovelRepository();
  const { firestore } = useFirebase();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!novel.id || !firestore || !sessionId) return;

    const sessionRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions', sessionId);

    const unsubscribe = onSnapshot(
      sessionRef,
      (snapshot) => {
        setLoading(false);
        if (snapshot.exists()) {
          const data = snapshot.data();
          setSession(data);

          // Check if awaiting review
          if (data.status === 'awaiting_review') {
            navigate({ to: '/import/review/$sessionId', params: { sessionId } });
          } else if (data.status === 'completed') {
            navigate({ to: '/' });
          } else if (data.status === 'failed') {
            setErrorMsg(data.error || 'Import pipeline failed during processing.');
          }
        } else {
          setErrorMsg('Import session not found.');
        }
      },
      (err) => {
        console.error('Error listening to import session:', err);
        setErrorMsg('Failed to listen to import session updates.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [novel, firestore, sessionId, navigate]);

  // Determine current active step in stepper
  const getActiveStepIndex = (status: string) => {
    const idx = PIPELINE_STEPS.findIndex((s) => s.status === status);
    if (idx !== -1) return idx;
    if (status === 'awaiting_review' || status === 'completed') return PIPELINE_STEPS.length;
    return 0;
  };

  const activeStep = session ? getActiveStepIndex(session.status) : 0;
  const currentStepInfo = PIPELINE_STEPS[activeStep] || { labelKey: 'import.loading_session' };

  if (!novel.id) {
    return (
      <Card sx={{ maxWidth: 500, mx: 'auto', mt: 4, textAlign: 'center', p: 4, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h2" gutterBottom>{t('entity_list.no_novel_title')}</Typography>
          <Typography variant="body1" color="text.secondary">
            {t('import.select_novel_desc')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 2 }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" color="text.secondary">
          {t('import.loading_session')}
        </Typography>
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 600, mx: 'auto' }}>
        <Card sx={{ mt: 4, borderRadius: 4, border: '1px solid', borderColor: 'error.main', bgcolor: 'background.paper' }}>
          <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <ErrorIcon color="error" sx={{ fontSize: 60 }} />
            <Typography variant="h2" color="error.main">{t('import.failed_title')}</Typography>
            <Typography variant="body1" color="text.secondary">
              {errorMsg}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.disabled">
                {t('import.failed_instructions')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Page
      title={t('import.processing_title')}
      description={t('import.processing_desc')}
      layoutMode="scrolling"
    >

      <Card sx={{ mt: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 4 }}>
            <CircularProgress
              variant="indeterminate"
              size={80}
              thickness={4}
              color="primary"
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                {session ? `${Math.round(session.progress)}%` : '0%'}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h3" sx={{ mb: 4, fontWeight: 600 }}>
            {t(currentStepInfo.labelKey)}
          </Typography>

          <Stepper activeStep={activeStep} orientation="vertical" sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
            {PIPELINE_STEPS.map((step, index) => {
              const labelProps: {
                optional?: React.ReactNode;
                error?: boolean;
              } = {};
              if (session?.status === 'failed' && activeStep === index) {
                labelProps.error = true;
              }
              return (
                <Step key={step.status}>
                  <StepLabel {...labelProps}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: activeStep === index ? 600 : 400,
                        color: activeStep === index ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {t(step.labelKey)}
                    </Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </CardContent>
      </Card>
    </Page>
  );
}

export default function ImportProcessingPage() {
  const { hasFeature, loading } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasFeature('ai_assistance_allowed')) {
    return (
      <Card sx={{ maxWidth: 500, mx: 'auto', mt: 8, textAlign: 'center', p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h2" gutterBottom color="error.main" sx={{ fontWeight: 600 }}>
            {t('import.access_denied_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
            {t('import.access_denied_desc')}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate({ to: '/' })} sx={{ borderRadius: 2 }}>
            {t('import.return_to_dashboard')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <AuthGuard>
      <ImportProcessingContent />
    </AuthGuard>
  );
}
